/**
 * The dashboard's widget data through ONE request — GET /api/dashboard/summary
 * (RP-3, card hFtR1-FJ4llM). The server side, with every part's rows, columns
 * and order: api src/data/dashboard-summary.ts.
 *
 * Each widget still owns its query; only the fetch is shared. A widget's
 * queryFn asks `loadDashboardPart(part)`, and every part asked for in the same
 * tick — the widgets of one dashboard render — goes out as one
 * `?parts=a,b,c` call, one SQL statement on the server. A later refetch of one
 * widget (an invalidation, a retry) fetches just its own part.
 *
 * Keys, and so invalidation:
 *   - Parts that are exactly what a generated hook used to return keep that
 *     hook's key: the KPI counts (`<table>Keys.count()`) and the two
 *     comp-project windows (`compProjectsKeys.list(…)`, the same filters /
 *     limit / sort). Every invalidation that refreshed them still does, and
 *     other pages reading the same key share the cache (same content).
 *   - Parts composed from several tables (pinned properties, shared with me,
 *     the released-unviewed badge) live under `dashboardKeys`, which no table's
 *     invalidation reaches: main.tsx's MutationCache invalidates
 *     `dashboardKeys.all` after every successful mutation (as for the property
 *     page's deals), and they refetch on every mount (`staleTime: 0`), which
 *     covers writes made without a mutation hook (e.g. report release).
 */

import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../../http-client';
import { compProjectsKeys } from '../../generated_hooks/generated-comp_projects-hooks';
import { propertiesKeys } from '../../generated_hooks/generated-properties-hooks';
import { saleTransactionsKeys } from '../../generated_hooks/generated-sale_transactions-hooks';
import { leaseTransactionsKeys } from '../../generated_hooks/generated-lease_transactions-hooks';
import { ingestionJobsKeys } from '../../generated_hooks/generated-ingestion_jobs-hooks';
import type { CompProjectsRow } from '../../../types/generated_schemas/generated-comp_projects-schema';
import type { CompProjectSharesRow } from '../../../types/generated_schemas/generated-comp_project_shares-schema';
import type { PropertyPinsRow } from '../../../types/generated_schemas/generated-property_pins-schema';

export interface DashboardCounts {
  properties: number;
  saleTransactions: number;
  leaseTransactions: number;
  compProjects: number;
  ingestionJobs: number;
}

export interface DashboardPinnedProperty {
  id: string;
  name: string | null;
  address_line_1: string | null;
  city: string | null;
  state: string | null;
  full_address: string | null;
}

export interface DashboardSharedProject {
  id: string;
  title: string | null;
  created_by_user_profile_id: string | null;
}

export interface DashboardOwner {
  id: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  trig_user_email: string | null;
}

/** The Comp request turnaround card (part compRequestStats): seconds, null with no sample. */
export interface DashboardCompRequestStats {
  /** team: every request (the Research / Admin tier); me: the caller's own. */
  scope: 'team' | 'me';
  open: number;
  unassigned: number;
  untouched: number;
  pastDeadline: number;
  /** Requests touched (or assigned): the first-touch median's sample. */
  touched: number;
  medianFirstTouchSeconds: number | null;
  fulfilled: number;
  medianFulfilledSeconds: number | null;
}

/** A RUNNING ingestion job with runs still waiting on a reviewer. */
export interface DashboardIngestionJob {
  id: string;
  job_name: string | null;
  /** When the parse finished: since then it has waited on review. */
  completed_at: string;
  unsettled: number;
}

/** An admin-posted announcement shown today to the caller (dashboard cards 8). Days are yyyy-mm-dd. */
export interface DashboardAnnouncement {
  id: string;
  title: string;
  body: string;
  /** The roles it is for; null or empty = everyone. */
  audience_role_ids: string[] | null;
  starts_on: string;
  /** Last day shown; null = until removed. */
  ends_on: string | null;
  pinned: boolean;
  created_at: string;
}

export interface DashboardSummary {
  counts: DashboardCounts;
  /** The caller's pins (created_at, id) and their properties' display fields. */
  pinnedProperties: { pins: PropertyPinsRow[]; properties: DashboardPinnedProperty[] };
  /** The Research Queue's window: 200 oldest projects, then the visibility rule. */
  researchQueue: CompProjectsRow[];
  /** My Reports' window: 200 newest projects, then the visibility rule. */
  myRequests: CompProjectsRow[];
  /** The caller's active shares (granted_at, id), their visible projects and those projects' owners. */
  sharedWithMe: { shares: CompProjectSharesRow[]; projects: DashboardSharedProject[]; owners: DashboardOwner[] };
  /** Visible projects with a release newer than the caller's last view. */
  releasedUnviewed: string[];
  /** My Reports' window over the projects the caller created (the card's scope: me). */
  myOwnRequests: CompProjectsRow[];
  compRequestStats: DashboardCompRequestStats;
  /** Jobs waiting on review: their count, their unsettled records, the longest waiting. Left out without data_ingestion. */
  ingestionReview: { jobs: number; records: number; oldest: DashboardIngestionJob[] };
  /** Today's announcements for everyone or one of the caller's roles: pinned first, then the newest start. */
  announcements: DashboardAnnouncement[];
}
export type DashboardPart = keyof DashboardSummary;

/** Request order — a part set always yields the same URL (ETag-friendly). */
const PART_ORDER: DashboardPart[] = [
  'counts',
  'pinnedProperties',
  'researchQueue',
  'myRequests',
  'sharedWithMe',
  'releasedUnviewed',
  'myOwnRequests',
  'compRequestStats',
  'ingestionReview',
  'announcements',
];

export async function fetchDashboardSummary(parts: readonly DashboardPart[]): Promise<Partial<DashboardSummary>> {
  const wanted = PART_ORDER.filter((p) => parts.includes(p));
  const res = await httpClient.get<{ success: boolean; data: Partial<DashboardSummary> }>(
    `/dashboard/summary?parts=${wanted.join(',')}`,
  );
  return res.data;
}

let batch: { parts: Set<DashboardPart>; result: Promise<Partial<DashboardSummary>> } | null = null;

/** One part of the summary; parts asked for in the same tick share one request. */
export function loadDashboardPart<P extends DashboardPart>(part: P): Promise<DashboardSummary[P]> {
  if (!batch) {
    const parts = new Set<DashboardPart>();
    const result = new Promise<void>((resolve) => setTimeout(resolve, 0)).then(() => {
      batch = null;
      return fetchDashboardSummary([...parts]);
    });
    batch = { parts, result };
  }
  batch.parts.add(part);
  return batch.result.then((summary) => summary[part] as DashboardSummary[P]);
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  part: (part: DashboardPart, userId: string) => ['dashboard', part, userId] as const,
};

/** A composed part, under `dashboardKeys` (see the header for its freshness). */
export function useDashboardPart<
  P extends 'pinnedProperties' | 'sharedWithMe' | 'releasedUnviewed' | 'myOwnRequests' | 'compRequestStats' | 'ingestionReview' | 'announcements',
>(part: P, userId: string) {
  return useQuery({
    queryKey: dashboardKeys.part(part, userId),
    queryFn: () => loadDashboardPart(part),
    enabled: !!userId,
    staleTime: 0,
  });
}

const COUNT_KEYS = {
  properties: propertiesKeys.count(),
  saleTransactions: saleTransactionsKeys.count(),
  leaseTransactions: leaseTransactionsKeys.count(),
  compProjects: compProjectsKeys.count(),
  ingestionJobs: ingestionJobsKeys.count(),
} satisfies Record<keyof DashboardCounts, readonly unknown[]>;

/** One KPI count, under the generated `use<Table>Count()` key. */
export function useDashboardCount(field: keyof DashboardCounts) {
  return useQuery({
    queryKey: COUNT_KEYS[field],
    queryFn: () => loadDashboardPart('counts').then((counts) => counts[field]),
  });
}

/**
 * The generated `useCompProjectsList` options each window stood for — the key
 * is built from them exactly as that hook builds it. The server's windows
 * (RESEARCH_QUEUE_LIMIT / MY_REQUESTS_LIMIT and their order) must match;
 * the API's dashboard-summary parity test pins both.
 */
const PROJECT_WINDOWS = {
  researchQueue: { sortBy: 'created_at', sortOrder: 'asc', limit: 200 },
  myRequests: { sortBy: 'created_at', sortOrder: 'desc', limit: 200 },
} as const;

/** A comp-project window, under the generated list key for the same request. */
export function useDashboardCompProjectsList(part: keyof typeof PROJECT_WINDOWS) {
  const { limit, sortBy, sortOrder } = PROJECT_WINDOWS[part];
  return useQuery({
    queryKey: compProjectsKeys.list({}, { limit, offset: undefined, sortBy, sortOrder }),
    queryFn: () => loadDashboardPart(part),
  });
}

/**
 * My Reports' rows for the card's scope: 'me' = the projects the caller created (part myOwnRequests,
 * under `dashboardKeys` — no generated list answers it); otherwise the myRequests window, as above.
 */
export function useDashboardMyRequests(scope: 'me' | 'team' | undefined, userId: string) {
  const { limit, sortBy, sortOrder } = PROJECT_WINDOWS.myRequests;
  return useQuery<CompProjectsRow[]>(
    scope === 'me'
      ? { queryKey: dashboardKeys.part('myOwnRequests', userId), queryFn: () => loadDashboardPart('myOwnRequests'), enabled: !!userId, staleTime: 0 }
      : { queryKey: compProjectsKeys.list({}, { limit, offset: undefined, sortBy, sortOrder }), queryFn: () => loadDashboardPart('myRequests') },
  );
}
